import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// NOTE on localStorage token storage (security tradeoff):
// The backend sets httpOnly cookies for browser-based sessions (primary auth).
// localStorage tokens are a fallback for environments where cookies aren't available
// (e.g., non-browser API clients, dev tools). No sensitive user data beyond the
// JWT token is stored here. Tokens are short-lived (24h) and rotated on refresh.
const TOKEN_KEY = "manna_access_token";
const REFRESH_KEY = "manna_refresh_token";

const client = axios.create({
  baseURL: API,
  withCredentials: false,   // We use Bearer tokens — no cookies needed
});

// Attach token to every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401 && !err.config._retry) {
      err.config._retry = true;
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`,
            { refresh_token: refreshToken }
          );
          const newToken = data.access_token;
          localStorage.setItem(TOKEN_KEY, newToken);
          if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          err.config.headers["Authorization"] = `Bearer ${newToken}`;
          return client(err.config);
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
          window.location.href = "/login";
        }
      } else {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}

// Auth
export const authAPI = {
  login: (email, password) => client.post("/auth/login", { email, password }),
  logout: () => client.post("/auth/logout"),
  me: () => client.get("/auth/me"),
  forcedPasswordReset: (data) => client.post("/auth/forced-password-reset", data),
};

// Users & Roles & Kitchens
export const usersAPI = {
  getAll: (params) => client.get("/users", { params }),
  create: (data) => client.post("/users", data),
  update: (id, data) => client.put(`/users/${id}`, data),
  delete: (id) => client.delete(`/users/${id}`),
  reactivate: (id) => client.put(`/users/${id}/reactivate`),
  resetPassword: (id, password) => client.put(`/users/${id}/password`, { password }),
  getRoles: () => client.get("/roles"),
  createRole: (data) => client.post("/roles", data),
  updateRole: (id, data) => client.put(`/roles/${id}`, data),
  deleteRole: (id) => client.delete(`/roles/${id}`),
  getKitchens: () => client.get("/kitchens"),
  createKitchen: (data) => client.post("/kitchens", data),
  updateKitchen: (id, data) => client.put(`/kitchens/${id}`, data),
  deleteKitchen: (id) => client.delete(`/kitchens/${id}`),
  getNotifications: (limit) => client.get("/notifications", { params: { limit } }),
  markRead: (id) => client.put(`/notifications/${id}/read`),
  markAllRead: () => client.put("/notifications/read-all"),
};

// Admin Settings
export const adminAPI = {
  getMasterItems: (params) => client.get("/admin/master-items", { params }),
  createItem: (data) => client.post("/admin/master-items", data),
  updateItem: (id, data) => client.put(`/admin/master-items/${id}`, data),
  deleteItem: (id) => client.delete(`/admin/master-items/${id}`),
  updateMinStock: (id, min_stock) => client.put(`/admin/master-items/${id}/min-stock`, { min_stock }),
  getTargetPrices: () => client.get("/admin/target-prices"),
  createTargetPrice: (data) => client.post("/admin/target-prices", data),
  updateTargetPrice: (id, data) => client.put(`/admin/target-prices/${id}`, data),
  deleteTargetPrice: (id) => client.delete(`/admin/target-prices/${id}`),
};

// Logistics
export const logisticsAPI = {
  getRoutes: (params) => client.get("/logistics/routes", { params }),
  createRoute: (data) => client.post("/logistics/routes", data),
  updateRoute: (id, data) => client.put(`/logistics/routes/${id}`, data),
  updateSchool: (id, data) => client.put(`/logistics/school-master/${id}`, data),
  setSchoolLocation: (id, data) => client.put(`/logistics/school-master/${id}/location`, data),
  retireRoute: (id) => client.post(`/logistics/routes/${id}/retire`),
  unretireRoute: (id) => client.post(`/logistics/routes/${id}/unretire`),
  clearRouteDriverName: (id) => client.delete(`/logistics/routes/${id}/driver-name`),
  bulkClearRouteNames: () => client.post("/logistics/routes/bulk-clear-names"),
  // Route Import
  downloadRouteTemplate: () => `${API}/logistics/route-import/template`,
  downloadSchoolTemplate: () => `${API}/logistics/schools/import/template`,
  previewSchoolImportV2: (formData) => client.post("/logistics/schools/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  commitSchoolImportV2: (rows) => client.post("/logistics/schools/import/confirm", { rows }),
  // Import Formats
  listImportFormats: () => client.get("/logistics/schools/import/formats"),
  createImportFormat: (data) => client.post("/logistics/schools/import/formats", data),
  updateImportFormat: (id, data) => client.put(`/logistics/schools/import/formats/${id}`, data),
  deleteImportFormat: (id) => client.delete(`/logistics/schools/import/formats/${id}`),
  setDefaultFormat: (id) => client.post(`/logistics/schools/import/formats/${id}/set-default`),
  previewSampleHeaders: (formData) => client.post("/logistics/schools/import/formats/preview-sample", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  previewWithFormat: (formData, formatId) => {
    const url = formatId ? `/logistics/schools/import/with-format?format_id=${formatId}` : "/logistics/schools/import/with-format";
    return client.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  previewRouteImport: (formData) => client.post("/logistics/route-import/preview", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  commitRouteImport: (rows) => client.post("/logistics/route-import/commit", { rows }),
  // Route Merge
  mergeRoutes: (data) => client.post("/logistics/routes/merge", data),
  // Route Reset
  resetRouteData: (confirm) => client.post("/logistics/routes/reset", { confirm }),
  // Test data cleanup
  cleanupTestData: () => client.post("/logistics/cleanup-test-data"),
  syncSchoolRoutesFromMaster: () => client.post("/logistics/school-master/sync-routes-from-master"),
  // School-to-Route Bulk Import
  downloadImportTemplate: () => `${API}/logistics/school-import/template`,
  previewSchoolImport: (formData) => client.post("/logistics/school-import/preview", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  commitSchoolImport: (rows) => client.post("/logistics/school-import/commit", { rows }),
  deleteRoute: (id) => client.delete(`/logistics/routes/${id}`),
  assignVehicle: (routeId, data) => client.put(`/logistics/routes/${routeId}/assign-vehicle`, data),
  getVehicles: (params) => client.get("/logistics/vehicles", { params }),
  createVehicle: (data) => client.post("/logistics/vehicles", data),
  updateVehicle: (id, data) => client.put(`/logistics/vehicles/${id}`, data),
  deleteVehicle: (id) => client.delete(`/logistics/vehicles/${id}`),
  getCoordinators: (params) => client.get("/logistics/route-coordinators", { params }),
  createCoordinator: (data) => client.post("/logistics/route-coordinators", data),
  updateCoordinator: (id, data) => client.put(`/logistics/route-coordinators/${id}`, data),
  deleteCoordinator: (id) => client.delete(`/logistics/route-coordinators/${id}`),
  // School Master
  getSchoolMaster: (params) => client.get("/logistics/school-master", { params }),
  getSchoolMasterStats: () => client.get("/logistics/school-master/stats"),
  resetSchoolMaster: () => client.post("/logistics/school-master/reset"),
  bulkUploadSchoolMaster: (formData) => client.post("/logistics/school-master/bulk-upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  downloadSchoolMasterTemplate: () => `${API}/logistics/school-master/upload-template`,
  // Temp Assignments
  getTempAssignments: (params) => client.get("/logistics/temp-assignments", { params }),
  createTempAssignment: (data) => client.post("/logistics/temp-assignments", data),
  updateTempAssignment: (id, data) => client.put(`/logistics/temp-assignments/${id}`, data),
  deleteTempAssignment: (id) => client.delete(`/logistics/temp-assignments/${id}`),
  // New per-route temp assignments
  getTempCoordAssignments: (params) => client.get("/logistics/temp-coordinator-assignments", { params }),
  createTempCoordAssignment: (data) => client.post("/logistics/temp-coordinator-assignments", data),
  deleteTempCoordAssignment: (id) => client.delete(`/logistics/temp-coordinator-assignments/${id}`),
  getTempVehicleAssignments: (params) => client.get("/logistics/temp-vehicle-assignments", { params }),
  createTempVehicleAssignment: (data) => client.post("/logistics/temp-vehicle-assignments", data),
  deleteTempVehicleAssignment: (id) => client.delete(`/logistics/temp-vehicle-assignments/${id}`),
  // Coordinator merge
  mergeCoordinators: (data) => client.post("/logistics/coordinators/merge", data),
  getCoordinatorVariants: () => client.get("/logistics/coordinators/variants"),
  // Reports
  getDailyReport: (params) => client.get("/logistics/reports/daily", { params }),
  // Vehicle Maintenance
  getMaintenance: (params) => client.get("/logistics/maintenance", { params }),
  createMaintenance: (data) => client.post("/logistics/maintenance", data),
  deleteMaintenance: (id) => client.delete(`/logistics/maintenance/${id}`),
  // Fuel Log
  getFuelLog: (params) => client.get("/logistics/fuel-log", { params }),
  createFuelLog: (data) => client.post("/logistics/fuel-log", data),
  getFuelSummary: (params) => client.get("/logistics/fuel-log/summary", { params }),
};

// Security Gate
export const securityAPI = {
  getEntries: (params) => client.get("/security/entries", { params }),
  getEntry: (id) => client.get(`/security/entries/${id}`),
  createEntry: (data) => client.post("/security/entries", data),
  returnVessels: (id, data) => client.post(`/security/entries/${id}/return-vessels`, data),
  logVisitorExit: (id) => client.put(`/security/entries/${id}/visitor-exit`, {}),
  verifyEntry: (id) => client.put(`/security/entries/${id}/verify`, {}),
  rejectEntry: (id, reason) => client.put(`/security/entries/${id}/reject`, { reason }),
  reviseAndVerify: (id, data) => client.put(`/security/entries/${id}/revise-and-verify`, data),
  runOCR: (image_base64) => client.post("/security/ocr", { image_base64 }),
  resetAll: () => client.post("/security/reset-all"),
};

// Vessels
export const vesselAPI = {
  getSummary: (params) => client.get("/vessels/summary", { params }),
  getLogs: (params) => client.get("/vessels/all-logs", { params }),
  getBiweeklyReport: (params) => client.get("/vessels/bi-weekly-report", { params }),
  reset: (params) => client.post("/vessels/reset-biweekly", null, { params }),
  resetAll: () => client.post("/vessels/reset-all"),
  // Direct entry
  logEntry: (data) => client.post("/vessels/log-entry", data),
  deleteLogEntry: (id) => client.delete(`/vessels/log-entry/${id}`),
  // Inventory (Opening Stock + Damage Tracker)
  getInventory: (params) => client.get("/vessels/inventory", { params }),
  setOpeningStock: (data) => client.post("/vessels/inventory/opening-stock", data),
  reduceStock: (data) => client.post("/vessels/inventory/reduce-stock", data),
  getStockLogs: (params) => client.get("/vessels/stock-logs", { params }),
};

// Stores
export const storesAPI = {
  getStockSummary: (params) => client.get("/stores/stock-summary", { params }),
  getInward: (params) => client.get("/stores/stock-inward", { params }),
  createInward: (data) => client.post("/stores/stock-inward", data),
  getIssues: (params) => client.get("/stores/stock-issues", { params }),
  createIssue: (data) => client.post("/stores/stock-issue", data),
  // Stock Requisitions
  getRequests: (params) => client.get("/stores/requests", { params }),
  createRequest: (data) => client.post("/stores/requests", data),
  approveRequest: (id) => client.post(`/stores/requests/${id}/approve`),
  rejectRequest: (id, data) => client.post(`/stores/requests/${id}/reject`, data),
  fulfillRequest: (id, data) => client.post(`/stores/requests/${id}/fulfill`, data),
  getRequest: (id) => client.get(`/stores/requests/${id}`),
  // Gate 2: confirm
  confirmIssue: (id, data) => client.post(`/stores/stock-issue/${id}/confirm`, data),
  getPendingConfirmations: (params) => client.get("/stores/pending-confirmations", { params }),
  getTransferVariances: (params) => client.get("/stores/transfer-variances", { params }),
  // Gate 1: variance report
  getReceivingVarianceReport: (params) => client.get("/stores/receiving-variance-report", { params }),
  getSettings: () => client.get("/stores/settings"),
  updateSettings: (data) => client.put("/stores/settings", data),
  // Gate 3: stock count
  submitStockCount: (data) => client.post("/stores/stock-count", data),
  getStockCountVariances: (params) => client.get("/stores/stock-count-variances", { params }),
  approveCountVariance: (id, data) => client.post(`/stores/stock-count-variances/${id}/approve`, data),
  rejectCountVariance: (id, data) => client.post(`/stores/stock-count-variances/${id}/reject`, data),
  getIndents: (params) => client.get("/stores/indents", { params }),
  createIndent: (data) => client.post("/stores/indent", data),
  updateIndentStatus: (id, status) => client.put(`/stores/indent/${id}/status`, { status }),
  getMinStockAlerts: (params) => client.get("/stores/min-stock-alerts", { params }),
  getExpiringStock: (params) => client.get("/stores/expiring-stock", { params }),
  getBatchStock: (params) => client.get("/stores/batch-stock", { params }),
  writeOffBatch: (batchId) => client.post(`/stores/write-off-batch/${batchId}`),
  bulkIssue: (data) => client.post("/stores/stock-issue/bulk", data),
  getPendingGateEntries: (params) => client.get("/stores/pending-gate-entries", { params }),
  linkGateEntry: (entryId) => client.post(`/stores/link-gate-entry/${entryId}`),
};

// Purchase
export const purchaseAPI = {
  getIndents: (params) => client.get("/purchase/indents", { params }),
  updateIndentStatus: (id, status) => client.put(`/stores/indent/${id}/status`, { status }),
  getOrders: (params) => client.get("/purchase/orders", { params }),
  createOrder: (data) => client.post("/purchase/orders", data),
  updateOrderStatus: (id, status) => client.put(`/purchase/orders/${id}/status`, { status }),
  deleteOrder: (id) => client.delete(`/purchase/orders/${id}`),
  getQuoteAnalysis: (params) => client.get("/purchase/quote-analysis", { params }),
};

// Tasks
export const tasksAPI = {
  getAll: (params) => client.get("/tasks", { params }),
  create: (data) => client.post("/tasks", data),
  update: (id, data) => client.put(`/tasks/${id}`, data),
  delete: (id) => client.delete(`/tasks/${id}`),
  addComment: (id, comment) => client.post(`/tasks/${id}/comments`, { comment }),
  addUpdate:  (id, text)    => client.post(`/tasks/${id}/updates`,  { text }),
};

// Reports
export const reportsAPI = {
  getStockSummary: (params) => client.get("/reports/stock-summary", { params }),
  getVesselReport: (params) => client.get("/reports/vessel-report", { params }),
  getSecurityEntries: (params) => client.get("/reports/security-entries", { params }),
  getDashboardStats: (params) => client.get("/reports/dashboard-stats", { params }),
};

// Menu / Recipe Management
export const menuAPI = {
  // Menu Items
  getItems: (params) => client.get("/menu/items", { params }),
  createItem: (data) => client.post("/menu/items", data),
  updateItem: (id, data) => client.put(`/menu/items/${id}`, data),
  deleteItem: (id) => client.delete(`/menu/items/${id}`),
  // Recipes
  getRecipes: (params) => client.get("/menu/recipes", { params }),
  createRecipe: (data) => client.post("/menu/recipes", data),
  updateRecipe: (id, data) => client.put(`/menu/recipes/${id}`, data),
  deleteRecipe: (id) => client.delete(`/menu/recipes/${id}`),
  parseRecipeDoc: (formData) => client.post("/menu/recipes/import-parse", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }),
  confirmRecipeImport: (data) => client.post("/menu/recipes/import-confirm", data),
  // Daily Plans
  getPlans: (params) => client.get("/menu/daily-plan", { params }),
  getPlanByDate: (date, kitchen_id) => client.get(`/menu/daily-plan/${date}/${kitchen_id}`),
  savePlan: (data) => client.post("/menu/daily-plan", data),
  updatePlanStatus: (id, status) => client.put(`/menu/daily-plan/${id}/status`, { status }),
  approvePlanEmergency: (plan_id, emergency_index) => client.post(`/menu/daily-plan/${plan_id}/emergency-approve`, { emergency_index }),
  // Stock Calculator
  calculateStock: (date, kitchen_id) => client.get("/menu/calculate-stock", { params: { date, kitchen_id } }),
  // Nutrition Calculator
  calcNutrition: (params) => client.get("/menu/nutrition", { params }),
};

// Attendance / Face Recognition
export const attendanceAPI = {
  // Staff
  getStaff: (params) => client.get("/attendance/staff", { params }),
  createStaff: (data) => client.post("/attendance/staff", data),
  updateStaff: (id, data) => client.put(`/attendance/staff/${id}`, data),
  deleteStaff: (id) => client.delete(`/attendance/staff/${id}`),
  registerFace: (id, data) => client.post(`/attendance/staff/${id}/register-face`, data),
  // Shifts
  getShifts: (params) => client.get("/attendance/shifts", { params }),
  createShift: (data) => client.post("/attendance/shifts", data),
  updateShift: (id, data) => client.put(`/attendance/shifts/${id}`, data),
  deleteShift: (id) => client.delete(`/attendance/shifts/${id}`),
  // Recognition & Attendance
  recognize: (data) => client.post("/attendance/recognize", data),
  checkIn: (data) => client.post("/attendance/check-in", data),
  checkOut: (data) => client.post("/attendance/check-out", data),
  // Records & Reports
  getRecords: (params) => client.get("/attendance/records", { params }),
  getTodaySummary: (kitchen_id) => client.get(`/attendance/today/${kitchen_id}`),
  getReport: (params) => client.get("/attendance/report/summary", { params }),
};

// HR Module
export const hrAPI = {
  // Departments
  getDepartments: () => client.get("/hr/departments"),
  createDepartment: (data) => client.post("/hr/departments", data),
  updateDepartment: (id, data) => client.put(`/hr/departments/${id}`, data),
  deleteDepartment: (id) => client.delete(`/hr/departments/${id}`),
  // Employees
  getEmployees: (params) => client.get("/hr/employees", { params }),
  createEmployee: (data) => client.post("/hr/employees", data),
  updateEmployee: (id, data) => client.put(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => client.delete(`/hr/employees/${id}`),
  // Subcontractors
  getSubcontractors: (params) => client.get("/hr/subcontractors", { params }),
  createSubcontractor: (data) => client.post("/hr/subcontractors", data),
  updateSubcontractor: (id, data) => client.put(`/hr/subcontractors/${id}`, data),
  deleteSubcontractor: (id) => client.delete(`/hr/subcontractors/${id}`),
  // Workers
  getWorkers: (params) => client.get("/hr/workers", { params }),
  createWorker: (data) => client.post("/hr/workers", data),
  updateWorker: (id, data) => client.put(`/hr/workers/${id}`, data),
  deleteWorker: (id) => client.delete(`/hr/workers/${id}`),
};

// Asset Management
export const assetAPI = {
  getAssets: (params) => client.get("/assets", { params }),
  getAlerts: (params) => client.get("/assets/alerts", { params }),
  createAsset: (data) => client.post("/assets", data),
  updateAsset: (id, data) => client.put(`/assets/${id}`, data),
  deleteAsset: (id) => client.delete(`/assets/${id}`),
};

// Operations (Field Visits, Vehicle Usage, Tray Inventory, OT)
export const operationsAPI = {
  // Field Visits
  getFieldVisits: (params) => client.get("/field-visits", { params }),
  getFieldVisit: (id) => client.get(`/field-visits/${id}`),
  createFieldVisit: (data) => client.post("/field-visits", data),
  updateFieldVisit: (id, data) => client.put(`/field-visits/${id}`, data),
  deleteFieldVisit: (id) => client.delete(`/field-visits/${id}`),
  // Vehicle Usage
  getVehicleUsage: (params) => client.get("/vehicle-usage", { params }),
  logVehicleUsage: (data) => client.post("/vehicle-usage", data),
  getVehicleReport: (params) => client.get("/vehicle-usage/report", { params }),
  // Tray Inventory
  getTrayInventory: (params) => client.get("/tray-inventory", { params }),
  getTrayMovements: (params) => client.get("/tray-movements", { params }),
  addTrayMovement: (data) => client.post("/tray-movements", data),
  // OT
  approveOT: (recordId, data) => client.put(`/attendance/ot-approval/${recordId}`, data),
  getMonthlyReport: (params) => client.get("/attendance/monthly-report", { params }),
};

// Notifications
export const notificationAPI = {
  getVapidKey: () => client.get("/notifications/vapid-key"),
  subscribe: (data) => client.post("/notifications/subscribe", data),
  unsubscribe: (data) => client.delete("/notifications/unsubscribe", { data }),
  testPush: () => client.post("/notifications/test"),
};

// Holiday Calendar
export const holidayAPI = {
  getHolidays: (params) => client.get("/holidays", { params }),
  getHolidaysInRange: (params) => client.get("/holidays/range", { params }),
  getWorkingDays: (params) => client.get("/holidays/working-days", { params }),
  createHoliday: (data) => client.post("/holidays", data),
  updateHoliday: (id, data) => client.put(`/holidays/${id}`, data),
  deleteHoliday: (id) => client.delete(`/holidays/${id}`),
};

// Delivery Tracking
export const deliveryAPI = {
  getStatus: (params) => client.get("/delivery/status", { params }),
  getSummary: (params) => client.get("/delivery/summary", { params }),
  updateDelivery: (data) => client.post("/delivery/update", data),
  bulkUpdate: (data) => client.post("/delivery/bulk-update", data),
  initRoute: (params) => client.get("/delivery/init-route", { params }),
};

// Quality Inspection
export const qualityAPI = {
  getFoodQuality: (params) => client.get("/quality/food", { params }),
  createFoodQuality: (data) => client.post("/quality/food", data),
  updateFoodQuality: (id, data) => client.put(`/quality/food/${id}`, data),
  getMaterialQuality: (params) => client.get("/quality/material", { params }),
  createMaterialQuality: (data) => client.post("/quality/material", data),
  getMaterialRecord: (id) => client.get(`/quality/material/${id}`),
};

// Recipe Cost Calculator
export const recipeCostAPI = {
  calculate: (params) => client.get("/recipe-cost/calculate", { params }),
  getHistory: (params) => client.get("/recipe-cost/history", { params }),
};

// GPS / Vehicle Tracking
export const gpsAPI = {
  getLiveLocations: (params) => client.get("/gps/live", { params }),
  updateLocation: (data) => client.post("/gps/location", data),
  addCheckpoint: (data) => client.post("/gps/checkpoint", data),
  getCheckpoints: (params) => client.get("/gps/checkpoints", { params }),
  getTrackingUrl: (vehicleNumber, params) => client.get(`/gps/tracking-url/${vehicleNumber}`, { params }),
};

// Inspector Visits
export const inspectorAPI = {
  getVisits: (params) => client.get("/inspector-visits", { params }),
  createVisit: (data) => client.post("/inspector-visits", data),
  updateVisit: (id, data) => client.put(`/inspector-visits/${id}`, data),
  updateAction: (visitId, actionIdx, data) => client.put(`/inspector-visits/${visitId}/action/${actionIdx}`, data),
  deleteVisit: (id) => client.delete(`/inspector-visits/${id}`),
  getSummary: (params) => client.get("/inspector-visits/summary", { params }),
};

// AI Analytics
export const aiAPI = {  chat: (data) => client.post("/ai/chat", data),
  weeklyReport: (data) => client.post("/ai/report/weekly", data),
  detectAnomalies: (params) => client.get("/ai/anomalies", { params }),
  stockForecast: (params) => client.get("/ai/stock-forecast", { params }),
  getChatHistory: (params) => client.get("/ai/chat-history", { params }),
  diagnose: (params) => client.post("/ai/diagnose", null, { params }),
  applyFix: (data) => client.post("/ai/apply-fix", data),
  getLogs: (params) => client.get("/ai/logs", { params }),
  analyzeLogs: (data) => client.post("/ai/analyze-logs", data),
  getFixHistory: (params) => client.get("/ai/fix-history", { params }),
  requestClarification: (data) => client.post("/ai/clarify", data),
};

// Vendor Management
export const vendorAPI = {
  getVendors: (params) => client.get("/vendors", { params }),
  createVendor: (data) => client.post("/vendors", data),
  updateVendor: (id, data) => client.put(`/vendors/${id}`, data),
  deleteVendor: (id) => client.delete(`/vendors/${id}`),
  updateRating: (id, rating) => client.put(`/vendors/${id}/rate`, { rating }),
  getPerformance: (id) => client.get(`/vendors/${id}/performance`),
  getRateContracts: (params) => client.get("/vendors/rate-contracts", { params }),
  getBestRate: (itemId, params) => client.get(`/vendors/rate-contracts/best/${itemId}`, { params }),
  createRateContract: (data) => client.post("/vendors/rate-contracts", data),
  updateRateContract: (id, data) => client.put(`/vendors/rate-contracts/${id}`, data),
  deleteRateContract: (id) => client.delete(`/vendors/rate-contracts/${id}`),
  getQuoteRequests: (params) => client.get("/vendors/quote-requests", { params }),
  createQuoteRequest: (data) => client.post("/vendors/quote-requests", data),
  updateQuoteStatus: (id, status) => client.put(`/vendors/quote-requests/${id}/status`, { status }),
  getQuoteResponses: (reqId) => client.get(`/vendors/quote-requests/${reqId}/responses`),
  addQuoteResponse: (data) => client.post("/vendors/quote-responses", data),
  getQuoteComparison: (reqId) => client.get(`/vendors/quote-requests/${reqId}/comparison`),
  getScorecard: () => client.get("/vendors/performance/scorecard"),
};

// School Indent / Strength
export const schoolIndentAPI = {
  getEntries: (params) => client.get("/school-indent/entries", { params }),
  getSummary: (params) => client.get("/school-indent/summary", { params }),
  createEntry: (data) => client.post("/school-indent/entry", data),
  bulkEntry: (data) => client.post("/school-indent/bulk-entry", data),
  getFoodRequirement: (params) => client.get("/school-indent/food-requirement", { params }),
};

// Accounts & Billing
export const accountsAPI = {
  getBillingRates: (params) => client.get("/accounts/billing-rates", { params }),
  createBillingRate: (data) => client.post("/accounts/billing-rates", data),
  updateBillingRate: (id, data) => client.put(`/accounts/billing-rates/${id}`, data),
  deleteBillingRate: (id) => client.delete(`/accounts/billing-rates/${id}`),
  generateBilling: (data) => client.post("/accounts/generate-billing", data),
  getStatements: (params) => client.get("/accounts/statements", { params }),
  getStatement: (id) => client.get(`/accounts/statements/${id}`),
  updateStatementStatus: (id, status) => client.put(`/accounts/statements/${id}/status`, { status }),
  getDashboard: (params) => client.get("/accounts/dashboard", { params }),
  importDEOIndent: (data) => client.post("/accounts/deo-indent/import", data),
  getDEOIndent: (params) => client.get("/accounts/deo-indent", { params }),
  exportBillingUrl: (period, kitchenId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const params = kitchenId ? `?kitchen_id=${kitchenId}` : "";
    return { url: `${process.env.REACT_APP_BACKEND_URL}/api/accounts/billing-export/${period}${params}`, token };
  },
  exportVoucherUrl: (period, kitchenId, district) => {
    const token = localStorage.getItem(TOKEN_KEY);
    let params = kitchenId ? `?kitchen_id=${kitchenId}` : "?";
    if (district) params += `&district=${encodeURIComponent(district)}`;
    return { url: `${process.env.REACT_APP_BACKEND_URL}/api/accounts/billing-voucher/${period}${params}`, token };
  },
};

// Gmail Integration
export const gmailAPI = {
  getStatus: () => client.get("/gmail/status"),
  connect: () => client.get("/gmail/connect"),
  disconnect: () => client.post("/gmail/disconnect"),
  sendRFQ: (data) => client.post("/gmail/send-rfq", data),
  fetchReplies: () => client.post("/gmail/fetch-replies"),
  getPendingQuotes: (status) => client.get("/gmail/pending-quotes", { params: status ? { status } : {} }),
  approveQuote: (pendingId, data) => client.post(`/gmail/pending-quotes/${pendingId}/approve`, data),
  rejectQuote: (pendingId) => client.post(`/gmail/pending-quotes/${pendingId}/reject`),
  updatePendingQuote: (pendingId, data) => client.put(`/gmail/pending-quotes/${pendingId}`, data),
};

// Production Planning
export const productionAPI = {
  getCookSheet: (params) => client.get("/production/cook-sheet", { params }),
  confirmCookSheet: (data) => client.post("/production/cook-sheet/confirm", data),
  updateStatus: (id, data) => client.put(`/production/cook-sheet/${id}/status`, data),
  getHistory: (params) => client.get("/production/cook-sheet/history", { params }),
  getWaste: (params) => client.get("/production/waste", { params }),
  createWaste: (data) => client.post("/production/waste", data),
  deleteWaste: (id) => client.delete(`/production/waste/${id}`),
};

// School Compliance
export const complianceAPI = {
  getSMCTasting: (params) => client.get("/compliance/smc-tasting", { params }),
  createSMCTasting: (data) => client.post("/compliance/smc-tasting", data),
  updateSMCTasting: (id, data) => client.put(`/compliance/smc-tasting/${id}`, data),
  deleteSMCTasting: (id) => client.delete(`/compliance/smc-tasting/${id}`),
  getSMCStats: (params) => client.get("/compliance/smc-tasting/stats", { params }),
  getDeliveryConfirmations: (params) => client.get("/compliance/delivery-confirmation", { params }),
  createDeliveryConfirmation: (data) => client.post("/compliance/delivery-confirmation", data),
  getPendingDeliveries: (params) => client.get("/compliance/delivery-confirmation/pending", { params }),
  getComplaints: (params) => client.get("/compliance/complaints", { params }),
  createComplaint: (data) => client.post("/compliance/complaints", data),
  updateComplaint: (id, data) => client.put(`/compliance/complaints/${id}`, data),
  getComplaintStats: (params) => client.get("/compliance/complaints/stats", { params }),
};

// Utility Costs
export const utilityAPI = {
  getCosts: (params) => client.get("/utility", { params }),
  createCost: (data) => client.post("/utility", data),
  updateCost: (id, data) => client.put(`/utility/${id}`, data),
  deleteCost: (id) => client.delete(`/utility/${id}`),
  getSummary: (params) => client.get("/utility/summary", { params }),
};

export default client;


// Route Planner
export const routePlannerAPI = {
  // Schools
  getSchools: (params) => client.get("/route-planner/schools", { params }),
  setSchoolLocation: (id, data) => client.put(`/route-planner/schools/${id}/location`, data),
  geocodeAll: () => client.post("/route-planner/geocode-all"),
  geocodeStatus: () => client.get("/route-planner/geocode-status"),
  getUnassigned: (params) => client.get("/route-planner/unassigned-schools", { params }),
  searchNominatim: (query) => client.post("/route-planner/schools/search-nominatim", { query }),
  // Routes
  getRoutes: (params) => client.get("/route-planner/routes", { params }),
  createRoute: (data) => client.post("/route-planner/routes", data),
  updateRoute: (id, data) => client.put(`/route-planner/routes/${id}`, data),
  retireRoute: (id) => client.post(`/route-planner/routes/${id}/retire`),
  // Route versions
  getRouteVersions: (routeId) => client.get(`/route-planner/routes/${routeId}/versions`),
  getRoutesAsOf: (params) => client.get("/route-planner/routes-as-of", { params }),
  // Migration
  migrateStops: () => client.post("/route-planner/migrate-stops"),
  // Stops
  addSchool: (routeId, data) => client.post(`/route-planner/routes/${routeId}/add-school`, data),
  removeSchool: (routeId, schoolId) => client.delete(`/route-planner/routes/${routeId}/schools/${schoolId}`),
  reorderStops: (routeId, stops) => client.put(`/route-planner/routes/${routeId}/reorder`, { stops }),
  moveSchools: (data) => client.post("/route-planner/schools/move", data),
  // Logs
  getChangeLog: (params) => client.get("/route-planner/change-log", { params }),
  // Driver
  getDriverRoute: (routeNumber, date) => client.get(`/route-planner/driver-route/${routeNumber}`, { params: { date } }),
};

// Admin Backup & Restore
export const backupAPI = {
  status: () => client.get("/admin/backup/status"),
  runNow: () => client.post("/admin/backup/run-now"),
  download: (backupId) => backupId
    ? `${API}/admin/backup/download?backup_id=${backupId}`
    : `${API}/admin/backup/download`,
  validate: (formData) => client.post("/admin/backup/restore/validate", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  applyRestore: (formData, collections) => client.post(
    `/admin/backup/restore/apply${collections ? `?collections=${collections}` : ""}`,
    formData, { headers: { "Content-Type": "multipart/form-data" } }
  ),
};

// Logistics Entities (Vehicle, Driver, Coordinator masters + assignments)
export const logisticsEntitiesAPI = {
  // Vehicles
  getVehicles: (params) => client.get("/logistics-entities/vehicles", { params }),
  createVehicle: (data) => client.post("/logistics-entities/vehicles", data),
  updateVehicle: (id, data) => client.put(`/logistics-entities/vehicles/${id}`, data),
  retireVehicle: (id) => client.delete(`/logistics-entities/vehicles/${id}`),
  // Drivers
  getDrivers: (params) => client.get("/logistics-entities/drivers", { params }),
  createDriver: (data) => client.post("/logistics-entities/drivers", data),
  updateDriver: (id, data) => client.put(`/logistics-entities/drivers/${id}`, data),
  retireDriver: (id) => client.delete(`/logistics-entities/drivers/${id}`),
  // Coordinator master
  getCoordinators: () => client.get("/logistics-entities/coordinators"),
  createCoordinator: (data) => client.post("/logistics-entities/coordinators", data),
  importCoordinators: () => client.post("/logistics-entities/coordinators/import-from-routes"),
  // Route-Vehicle-Driver assignments
  getRouteVehicleAssignments: (params) => client.get("/logistics-entities/assignments/route-vehicle", { params }),
  assignVehicleToRoute: (data) => client.post("/logistics-entities/assignments/route-vehicle", data),
  // Effective-state resolver
  getEffectiveAssignment: (routeId, date) => client.get("/logistics-entities/effective", { params: { route_id: routeId, date } }),
  // Reconciliation
  getReconciliationIssues: () => client.get("/logistics-entities/reconciliation/issues"),
  importVehiclesFromRoutes: () => client.post("/logistics-entities/reconciliation/import-vehicles"),
  resolveOrphanSchools: () => client.post("/logistics-entities/reconciliation/resolve-orphan-schools"),
  // Change log
  getChangeLog: (params) => client.get("/logistics-entities/change-log", { params }),
};



export const coordinatorAPI = {
  getMyRoutes: () => client.get("/coordinator/my-routes"),
  getBriefing: () => client.get("/coordinator/briefing"),
  getPerformance: (days) => client.get("/coordinator/performance", { params: { days } }),
  getDispatch: (params) => client.get("/coordinator/dispatch", { params }),
  getVesselLoading: (params) => client.get("/coordinator/vessel-loading", { params }),
  confirmLoading: (routeId, data) => client.post(`/coordinator/routes/${routeId}/confirm-loading`, data),
  getSchools: () => client.get("/coordinator/schools"),
  setSchoolLocation: (schoolId, data) => client.put(`/coordinator/schools/${schoolId}/location`, data),
  submitVisit: (formData) => client.post("/coordinator/school-visits", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }),
  getVisits: (params) => client.get("/coordinator/school-visits", { params }),
  getAlerts: () => client.get("/coordinator/alerts"),
  acknowledgeAlert: (alertId) => client.post(`/coordinator/alerts/${alertId}/acknowledge`),
  getComplaints: (params) => client.get("/coordinator/complaints", { params }),
  addRcNotes: (complaintId, data) => client.put(`/coordinator/complaints/${complaintId}/rc-notes`, data),
};

export const callCentreAPI = {
  getDailyList: (params) => client.get("/call-centre/daily-list", { params }),
  logCall: (data) => client.post("/call-centre/logs", data),
  getLogs: (params) => client.get("/call-centre/logs", { params }),
  getSchoolHistory: (schoolId) => client.get(`/call-centre/school/${schoolId}/history`),
  getUncalled: (params) => client.get("/call-centre/uncalled", { params }),
  getDashboard: () => client.get("/call-centre/dashboard"),
  runAnalysis: () => client.post("/call-centre/analyse"),
};
